/**
 * Machine page › Operators & shifts — projections.
 *
 * Pure functions over the reliability context: who drove this machine on
 * each shift, how each operator's share of the events compares with their
 * share of the hours, the root-cause verdict on the machine's flagged
 * component, and any training need among the machine's operators.
 *
 * Transformation layer (layer 3 of the 4-layer architecture): no React, no
 * mutation, no clock.
 *
 * @see src/versions/v1/modules/fleet/MachineOperatorsTab.tsx
 * @see docs/logic/fleet/machine-operators.md
 */

import type {
  CrewCode,
  RootCause,
  RootCauseFinding,
  ShiftCode,
  TrainingNeed,
} from '../../schema/operations';
import {
  machineOperatorShares,
  rootCauses,
  shiftLog,
  trainingNeeds,
} from '../../shared/reliability';
import type { ReliabilityCtx } from '../../shared/reliability/useReliability';
import { OPERATOR_BY_ID } from '../../shared/seed/operators';

/** One row of the shift log, newest first. */
export interface ShiftRow {
  key: string;
  /** `08 Sep`. */
  dateLabel: string;
  date: string;
  shift: ShiftCode;
  crew: CrewCode;
  operatorId: string;
  operatorName: string;
  synthesised: boolean;
  hours: number;
  /** Operator-sensitive events, abuse included. */
  sensitiveEvents: number;
  /** Operator-sensitive events on the flagged component; equals `sensitiveEvents` with no finding. */
  componentEvents: number;
  allEvents: number;
  /** `E-BRK-TEMP-HIGH ×4 · W-BRK-TEMP ×1`. */
  codes: string;
  /** Driven by the operator the verdict names. */
  isTopOperator: boolean;
}

/** One operator's share of the machine's hours and of the flagged component's events. */
export interface ShareRow {
  operatorId: string;
  name: string;
  crew: CrewCode;
  synthesised: boolean;
  hours: number;
  events: number;
  /** 0–100, whole numbers. */
  hoursPct: number;
  eventPct: number;
  /** `eventPct / hoursPct`, one decimal; `null` without hours. */
  ratio: number | null;
  /** The verdict's concentrated operator, when the cause is operator or mixed. */
  concentrated: boolean;
}

/** The verdict card's content. */
export interface VerdictView {
  component: string;
  cause: RootCause;
  causeLabel: string;
  /** Band-pill class for the cause. */
  causeClass: string;
  confidencePct: number;
  events: number;
  pValueLabel: string | null;
  evidence: readonly string[];
  /** Who the cause routes to, in words. */
  routesTo: string;
}

/** A training need among this machine's operators. */
export interface MachineTrainingFlag {
  operatorId: string;
  name: string;
  crew: CrewCode;
  ratio: number;
  ratePer100h: number;
  peerRate: number;
  events: number;
  hours: number;
  recommendation: string;
  synthesised: boolean;
}

/** Everything the Operators & shifts tab renders. */
export interface MachineOperatorsView {
  machine: string;
  shifts: ShiftRow[];
  totals: { shifts: number; hours: number; sensitiveEvents: number; componentEvents: number; operators: number };
  /** Events the share chart counts: the flagged component's, or all sensitive events. */
  shareScope: string;
  shares: ShareRow[];
  /** One sentence reading the share chart, e.g. the concentration. */
  shareTakeaway: string | null;
  verdict: VerdictView | null;
  /** The machine's other classified components, one line each. */
  otherFindings: readonly { component: string; causeLabel: string; causeClass: string; confidencePct: number }[];
  training: MachineTrainingFlag[];
  /** Directory (real) operators on the machine, by name. */
  directoryOperators: string[];
  synthesisedOperators: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-09-08` → `08 Sep`. */
export function dayLabel(date: string): string {
  const [, m, d] = date.split('-');
  return `${d} ${MONTHS[Number(m) - 1] ?? m}`;
}

export const CAUSE_LABEL: Readonly<Record<RootCause, string>> = {
  operator: 'Operator',
  equipment: 'Equipment',
  mixed: 'Operator + equipment',
  insufficient: 'Not enough evidence',
};

/** Pill colour classes per cause — the condition scale's colours, read as severity; neutral for no call. */
export const CAUSE_CLASS: Readonly<Record<RootCause, string>> = {
  mixed: 'band-vpoor',
  operator: 'band-poor',
  equipment: 'band-fair',
  insufficient: 'bg-[var(--surface-3)] text-[var(--ink-secondary)]',
};

const ROUTES_TO: Readonly<Record<RootCause, string>> = {
  operator: 'Operating Supervisor, Superintendent and Mine Control',
  equipment: 'Reliability Engineer and Maintenance Planner',
  mixed: 'All five roles — operations and maintenance',
  insufficient: 'Reliability Engineer triage',
};

function verdictOf(f: RootCauseFinding): VerdictView {
  return {
    component: f.component,
    cause: f.cause,
    causeLabel: CAUSE_LABEL[f.cause],
    causeClass: CAUSE_CLASS[f.cause],
    confidencePct: Math.round(f.confidence * 100),
    events: f.events,
    pValueLabel:
      f.pValue === null ? null : f.pValue < 0.001 ? 'p < 0.001' : `p = ${f.pValue.toFixed(3)}`,
    evidence: f.evidence,
    routesTo: ROUTES_TO[f.cause],
  };
}

/** Read the share chart in one sentence: who carries more than their hours, or that nobody does. */
function takeaway(shares: readonly ShareRow[], lead: RootCauseFinding | null): string | null {
  const total = shares.reduce((s, r) => s + r.events, 0);
  if (!total) return null;
  const c = shares.find((r) => r.concentrated);
  if (c) {
    return `${c.name} carries ${c.eventPct}% of the events on ${c.hoursPct}% of the hours — ${c.ratio}× their share. That is a driving pattern, not chance${lead?.cause === 'mixed' ? ', on top of a worn component' : ''}.`;
  }
  const top = shares[0];
  return `Events follow the hours: the most events on one operator is ${top.eventPct}% on ${top.hoursPct}% of the hours${lead?.cause === 'equipment' ? ' — an equipment problem, not a driver' : ''}.`;
}

/**
 * The Operators & shifts view for one machine.
 *
 * - **Shift log**: `shiftLog()` newest first; `isTopOperator` marks the verdict's operator.
 * - **Flagged component**: the machine's first `rootCauses()` finding (mixed →
 *   operator → equipment → insufficient, then confidence). The share chart counts
 *   that component's operator-sensitive events; with no finding it counts all of them.
 * - **Training**: `trainingNeeds()` restricted to operators who drove this machine.
 */
export function machineOperatorsView(machine: string, ctx: ReliabilityCtx): MachineOperatorsView {
  const findings = rootCauses(ctx).filter((f) => f.machine === machine);
  const lead = findings[0] ?? null;
  const concentratedId =
    lead && (lead.cause === 'operator' || lead.cause === 'mixed') ? lead.topOperator?.operatorId ?? null : null;

  const log = shiftLog(ctx.attribution, machine);
  const componentEvents = new Map<string, number>();
  if (lead) {
    for (const e of ctx.attribution.events) {
      if (e.machine !== machine || e.component !== lead.component || e.sensitivity === 'none') continue;
      const k = `${e.date}-${e.shift}`;
      componentEvents.set(k, (componentEvents.get(k) ?? 0) + e.count);
    }
  }
  const shifts: ShiftRow[] = log
    .map((e) => ({
      key: `${e.date}-${e.shift}`,
      dateLabel: dayLabel(e.date),
      date: e.date,
      shift: e.shift,
      crew: e.crew,
      operatorId: e.operatorId,
      operatorName: e.operatorName,
      synthesised: OPERATOR_BY_ID[e.operatorId]?.synthesised ?? true,
      hours: e.hours,
      sensitiveEvents: e.sensitiveEvents,
      componentEvents: lead ? componentEvents.get(`${e.date}-${e.shift}`) ?? 0 : e.sensitiveEvents,
      allEvents: e.allEvents,
      codes: e.codes.map((c) => `${c.code} ×${c.count}`).join(' · '),
      isTopOperator: concentratedId !== null && e.operatorId === concentratedId,
    }))
    .reverse();

  const raw = machineOperatorShares(ctx.attribution, machine, lead ? { component: lead.component } : {});
  const shares: ShareRow[] = raw.map((s) => {
    const hoursPct = Math.round(s.hoursShare * 100);
    const eventPct = Math.round(s.eventShare * 100);
    return {
      operatorId: s.operatorId,
      name: s.name,
      crew: s.crew,
      synthesised: s.synthesised,
      hours: s.hours,
      events: s.events,
      hoursPct,
      eventPct,
      ratio: s.hoursShare > 0 ? Math.round((s.eventShare / s.hoursShare) * 10) / 10 : null,
      concentrated: s.operatorId === concentratedId,
    };
  });

  const onMachine = new Set(log.map((e) => e.operatorId));
  const training: MachineTrainingFlag[] = trainingNeeds(ctx.attribution)
    .filter((n: TrainingNeed) => onMachine.has(n.operatorId))
    .map((n) => ({
      operatorId: n.operatorId,
      name: n.name,
      crew: n.crew,
      ratio: Math.round(n.ratio * 10) / 10,
      ratePer100h: n.ratePer100h,
      peerRate: n.peerRate,
      events: n.events,
      hours: n.hours,
      recommendation: n.recommendation,
      synthesised: n.synthesised,
    }));

  const operators = [...onMachine].map((id) => OPERATOR_BY_ID[id]).filter(Boolean);

  return {
    machine,
    shifts,
    totals: {
      shifts: log.length,
      hours: Math.round(log.reduce((s, e) => s + e.hours, 0) * 10) / 10,
      sensitiveEvents: log.reduce((s, e) => s + e.sensitiveEvents, 0),
      componentEvents: shifts.reduce((s, r) => s + r.componentEvents, 0),
      operators: onMachine.size,
    },
    shareScope: lead ? `${lead.component} events` : 'operator-sensitive events, all components',
    shares,
    shareTakeaway: takeaway(shares, lead),
    verdict: lead ? verdictOf(lead) : null,
    otherFindings: findings.slice(1).map((f) => ({
      component: f.component,
      causeLabel: CAUSE_LABEL[f.cause],
      causeClass: CAUSE_CLASS[f.cause],
      confidencePct: Math.round(f.confidence * 100),
    })),
    training,
    directoryOperators: operators.filter((o) => !o.synthesised).map((o) => o.name),
    synthesisedOperators: operators.filter((o) => o.synthesised).length,
  };
}
